<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useStore } from 'vuex';
import { required, maxLength } from '@vuelidate/validators';
import useVuelidate from '@vuelidate/core';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Slider from 'dashboard/components-next/slider/Slider.vue';
import QueryBuilderInstructionEditor from './components/QueryBuilderInstructionEditor.vue';
import CustomGeoAreasEditor from './components/CustomGeoAreasEditor.vue';

const props = defineProps({
  selectedKnowledgebase: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['on-close']);

const store = useStore();

const name = ref('');
const description = ref('');
const queryBuilderInstructions = ref('');
const optimizedQuery = ref(false);
const disableMatchFilter = ref(false);
const indexMedia = ref(false);
const hybridFactor = ref(1.5);
const maxDistanceBoost = ref(0.5);
const defaultGeoMode = ref(null);
const geoRadiusCity = ref(5000);
const geoRadiusRegion = ref(20000);
const geoRadiusAddress = ref(1000);
const customGeoAreas = ref([]);
const showAdvanced = ref(false);

const v$ = useVuelidate(
  {
    name: { required },
    description: { required },
    queryBuilderInstructions: { maxLength: maxLength(1000) },
  },
  { name, description, queryBuilderInstructions, disableMatchFilter }
);
const { t } = useI18n();

const isSystemKnowledgebase = computed(
  () => !!props.selectedKnowledgebase?.system
);

const uiFlags = computed(() => store.getters['knowledgebase/getUIFlags']);

const hybridFactorLevel = computed(() => {
  if (hybridFactor.value <= 1.0)
    return t('LIBRARIES.EDIT.ADVANCED_SETTINGS.LEVELS.NONE');
  if (hybridFactor.value <= 1.3)
    return t('LIBRARIES.EDIT.ADVANCED_SETTINGS.LEVELS.LOW');
  if (hybridFactor.value <= 1.6)
    return t('LIBRARIES.EDIT.ADVANCED_SETTINGS.LEVELS.MEDIUM');
  return t('LIBRARIES.EDIT.ADVANCED_SETTINGS.LEVELS.HIGH');
});

const maxDistanceBoostLevel = computed(() => {
  if (maxDistanceBoost.value <= 0.0)
    return t('LIBRARIES.EDIT.ADVANCED_SETTINGS.LEVELS.NONE');
  if (maxDistanceBoost.value <= 0.3)
    return t('LIBRARIES.EDIT.ADVANCED_SETTINGS.LEVELS.LOW');
  if (maxDistanceBoost.value <= 0.6)
    return t('LIBRARIES.EDIT.ADVANCED_SETTINGS.LEVELS.MEDIUM');
  return t('LIBRARIES.EDIT.ADVANCED_SETTINGS.LEVELS.HIGH');
});

const pageTitle = computed(
  () => `${t('LIBRARIES.EDIT.LABEL')} - ${props.selectedKnowledgebase.name}`
);

const setFormValues = () => {
  name.value = props.selectedKnowledgebase.name || '';
  description.value = props.selectedKnowledgebase.description || '';
  queryBuilderInstructions.value =
    props.selectedKnowledgebase.query_builder_instructions || '';
  optimizedQuery.value = props.selectedKnowledgebase.optimized_query || false;
  disableMatchFilter.value =
    props.selectedKnowledgebase.disable_match_filter || false;
  indexMedia.value = props.selectedKnowledgebase.index_media || false;
  hybridFactor.value = props.selectedKnowledgebase.hybrid_factor || 1.5;
  maxDistanceBoost.value =
    props.selectedKnowledgebase.max_distance_boost || 0.5;
  defaultGeoMode.value = props.selectedKnowledgebase.default_geo_mode || null;
  geoRadiusCity.value = props.selectedKnowledgebase.geo_radius_city ?? 5000;
  geoRadiusRegion.value =
    props.selectedKnowledgebase.geo_radius_region ?? 20000;
  geoRadiusAddress.value =
    props.selectedKnowledgebase.geo_radius_address ?? 1000;
  customGeoAreas.value = props.selectedKnowledgebase.custom_geo_areas || [];
  showAdvanced.value = isSystemKnowledgebase.value;
};

const resetForm = () => {
  setFormValues();
  v$.value.$reset();
};

const onClose = () => {
  emit('on-close');
};

const onCancel = () => {
  resetForm();
  onClose();
};

const editKnowledgebase = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) {
    return;
  }

  try {
    const payload = {
      id: props.selectedKnowledgebase.id,
      query_builder_instructions: queryBuilderInstructions.value,
      optimized_query: optimizedQuery.value,
      disable_match_filter: disableMatchFilter.value,
      index_media: indexMedia.value,
      hybrid_factor: hybridFactor.value,
      max_distance_boost: maxDistanceBoost.value,
      default_geo_mode: defaultGeoMode.value,
      geo_radius_city: geoRadiusCity.value,
      geo_radius_region: geoRadiusRegion.value,
      geo_radius_address: geoRadiusAddress.value,
      custom_geo_areas: customGeoAreas.value,
    };

    if (!isSystemKnowledgebase.value) {
      payload.name = name.value;
      payload.description = description.value;
    }

    await store.dispatch('knowledgebase/update', payload);
    useAlert(t('LIBRARIES.EDIT.API.SUCCESS_MESSAGE'));
    resetForm();
    onClose();
  } catch (error) {
    useAlert(t('LIBRARIES.EDIT.API.ERROR_MESSAGE'));
  }
};

watch(
  () => props.selectedKnowledgebase?.id,
  (newId, oldId) => {
    if (newId && newId !== oldId) {
      resetForm();
    }
  }
);

onMounted(() => {
  setFormValues();
});

defineExpose({ resetForm });
</script>

<template>
  <div class="h-auto overflow-auto flex flex-col">
    <woot-modal-header :header-title="pageTitle" />
    <form class="flex flex-col w-full" @submit.prevent="editKnowledgebase">
      <div class="w-full">
        <Input
          v-model="name"
          :label="$t('LIBRARIES.ADD.FORM.NAME.LABEL')"
          type="text"
          :disabled="isSystemKnowledgebase"
          :message="v$.name.$error ? $t('LIBRARIES.ADD.FORM.NAME.ERROR') : ''"
          :message-type="v$.name.$error ? 'error' : 'info'"
          :placeholder="$t('LIBRARIES.ADD.FORM.NAME.PLACEHOLDER')"
          @blur="v$.name.$touch"
        />

        <Input
          v-model="description"
          :label="$t('LIBRARIES.ADD.FORM.DESCRIPTION.LABEL')"
          type="text"
          :disabled="isSystemKnowledgebase"
          :message="
            v$.description.$error
              ? $t('LIBRARIES.ADD.FORM.DESCRIPTION.ERROR')
              : ''
          "
          :message-type="v$.description.$error ? 'error' : 'info'"
          :placeholder="$t('LIBRARIES.ADD.FORM.DESCRIPTION.PLACEHOLDER')"
          @blur="v$.description.$touch"
        />

        <!-- Advanced Settings Toggle -->
        <div class="mt-4">
          <button
            type="button"
            class="flex items-center gap-2 text-sm font-medium text-slate-600 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200 cursor-pointer transition-colors duration-200"
            :aria-expanded="showAdvanced"
            aria-controls="advanced-settings"
            @click="showAdvanced = !showAdvanced"
          >
            <span>
              {{
                showAdvanced
                  ? $t('LIBRARIES.EDIT.ADVANCED_SETTINGS.TOGGLE_HIDE')
                  : $t('LIBRARIES.EDIT.ADVANCED_SETTINGS.TOGGLE_SHOW')
              }}
            </span>
            <Icon
              :icon="
                showAdvanced ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'
              "
              class="w-4 h-4"
            />
          </button>
        </div>

        <!-- Advanced Settings -->
        <div v-if="showAdvanced" id="advanced-settings" class="mt-4 space-y-4">
          <!-- Content -->
          <div
            class="p-4 border border-slate-200 dark:border-slate-700 rounded-lg"
          >
            <h4
              class="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3"
            >
              {{ $t('LIBRARIES.EDIT.ADVANCED_SETTINGS.CONTENT.TITLE') }}
            </h4>
            <div>
              <label class="flex items-center gap-2 cursor-pointer">
                <input v-model="indexMedia" type="checkbox" />
                <span class="text-sm text-slate-700 dark:text-slate-300">{{
                  $t('LIBRARIES.EDIT.ADVANCED_SETTINGS.INDEX_MEDIA.LABEL')
                }}</span>
              </label>
              <p class="pl-6 text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                {{
                  $t('LIBRARIES.EDIT.ADVANCED_SETTINGS.INDEX_MEDIA.DESCRIPTION')
                }}
              </p>
            </div>
          </div>

          <!-- Search -->
          <div
            class="p-4 border border-slate-200 dark:border-slate-700 rounded-lg"
          >
            <h4
              class="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3"
            >
              {{ $t('LIBRARIES.EDIT.ADVANCED_SETTINGS.SEARCH.TITLE') }}
            </h4>

            <QueryBuilderInstructionEditor
              v-model="queryBuilderInstructions"
              :label="
                $t(
                  'LIBRARIES.EDIT.ADVANCED_SETTINGS.QUERY_BUILDER_INSTRUCTIONS.LABEL'
                )
              "
              :placeholder="
                $t(
                  'LIBRARIES.EDIT.ADVANCED_SETTINGS.QUERY_BUILDER_INSTRUCTIONS.PLACEHOLDER'
                )
              "
              :has-error="v$.queryBuilderInstructions.$error"
              :error-message="
                $t(
                  'LIBRARIES.EDIT.ADVANCED_SETTINGS.QUERY_BUILDER_INSTRUCTIONS.ERROR'
                )
              "
              @blur="v$.queryBuilderInstructions.$touch"
            />

            <div class="mt-4">
              <label class="flex items-center gap-2 cursor-pointer">
                <input v-model="optimizedQuery" type="checkbox" />
                <span class="text-sm text-slate-700 dark:text-slate-300">{{
                  $t('LIBRARIES.EDIT.ADVANCED_SETTINGS.OPTIMIZED_QUERY.LABEL')
                }}</span>
              </label>
              <p class="pl-6 text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                {{
                  $t(
                    'LIBRARIES.EDIT.ADVANCED_SETTINGS.OPTIMIZED_QUERY.DESCRIPTION'
                  )
                }}
              </p>
            </div>

            <div class="mt-3">
              <label class="flex items-center gap-2 cursor-pointer">
                <input v-model="disableMatchFilter" type="checkbox" />
                <span class="text-sm text-slate-700 dark:text-slate-300">{{
                  $t(
                    'LIBRARIES.EDIT.ADVANCED_SETTINGS.DISABLE_MATCH_FILTER.LABEL'
                  )
                }}</span>
              </label>
              <p class="pl-6 text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                {{
                  $t(
                    'LIBRARIES.EDIT.ADVANCED_SETTINGS.DISABLE_MATCH_FILTER.DESCRIPTION'
                  )
                }}
              </p>
            </div>

            <div class="mt-5">
              <Slider
                v-model="hybridFactor"
                :min="1.0"
                :max="2.0"
                :step="0.1"
                :label="
                  $t('LIBRARIES.EDIT.ADVANCED_SETTINGS.HYBRID_FACTOR.LABEL') +
                  ' — ' +
                  hybridFactorLevel
                "
              />
              <p class="text-xs text-slate-500 dark:text-slate-400 mt-1">
                {{
                  $t(
                    'LIBRARIES.EDIT.ADVANCED_SETTINGS.HYBRID_FACTOR.DESCRIPTION'
                  )
                }}
              </p>
            </div>
          </div>

          <!-- Location -->
          <div
            class="p-4 border border-slate-200 dark:border-slate-700 rounded-lg"
          >
            <h4
              class="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3"
            >
              {{ $t('LIBRARIES.EDIT.ADVANCED_SETTINGS.LOCATION.TITLE') }}
            </h4>

            <div>
              <label
                class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
              >
                {{
                  $t('LIBRARIES.EDIT.ADVANCED_SETTINGS.DEFAULT_GEO_MODE.LABEL')
                }}
              </label>
              <select
                v-model="defaultGeoMode"
                class="reset-base w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
              >
                <option :value="null">
                  {{
                    $t(
                      'LIBRARIES.EDIT.ADVANCED_SETTINGS.DEFAULT_GEO_MODE.OPTIONS.DEFAULT'
                    )
                  }}
                </option>
                <option value="isochrone">
                  {{
                    $t(
                      'LIBRARIES.EDIT.ADVANCED_SETTINGS.DEFAULT_GEO_MODE.OPTIONS.ISOCHRONE'
                    )
                  }}
                </option>
                <option value="auto">
                  {{
                    $t(
                      'LIBRARIES.EDIT.ADVANCED_SETTINGS.DEFAULT_GEO_MODE.OPTIONS.AUTO'
                    )
                  }}
                </option>
              </select>
              <p class="text-xs text-slate-500 dark:text-slate-400 mt-1">
                {{
                  $t(
                    'LIBRARIES.EDIT.ADVANCED_SETTINGS.DEFAULT_GEO_MODE.DESCRIPTION'
                  )
                }}
              </p>
            </div>

            <div class="mt-4">
              <Slider
                v-model="maxDistanceBoost"
                :min="0.0"
                :max="1.0"
                :step="0.1"
                :label="
                  $t(
                    'LIBRARIES.EDIT.ADVANCED_SETTINGS.MAX_DISTANCE_BOOST.LABEL'
                  ) +
                  ' — ' +
                  maxDistanceBoostLevel
                "
              />
              <p class="text-xs text-slate-500 dark:text-slate-400 mt-1">
                {{
                  $t(
                    'LIBRARIES.EDIT.ADVANCED_SETTINGS.MAX_DISTANCE_BOOST.DESCRIPTION'
                  )
                }}
              </p>
            </div>

            <h4
              class="text-sm font-medium text-slate-700 dark:text-slate-300 mt-5 mb-3"
            >
              {{ $t('LIBRARIES.EDIT.ADVANCED_SETTINGS.GEO_RADIUS.TITLE') }}
            </h4>
            <p class="text-xs text-slate-500 dark:text-slate-400 mb-3">
              {{
                $t('LIBRARIES.EDIT.ADVANCED_SETTINGS.GEO_RADIUS.DESCRIPTION')
              }}
            </p>

            <CustomGeoAreasEditor v-model="customGeoAreas" />

            <div class="grid grid-cols-3 gap-3">
              <div>
                <Input
                  v-model.number="geoRadiusAddress"
                  :label="
                    $t(
                      'LIBRARIES.EDIT.ADVANCED_SETTINGS.GEO_RADIUS.ADDRESS_LABEL'
                    )
                  "
                  type="number"
                  placeholder="1000"
                />
                <p class="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                  {{ $t('LIBRARIES.EDIT.ADVANCED_SETTINGS.GEO_RADIUS.UNIT') }}
                </p>
              </div>
              <div>
                <Input
                  v-model.number="geoRadiusCity"
                  :label="
                    $t('LIBRARIES.EDIT.ADVANCED_SETTINGS.GEO_RADIUS.CITY_LABEL')
                  "
                  type="number"
                  placeholder="5000"
                />
                <p class="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                  {{ $t('LIBRARIES.EDIT.ADVANCED_SETTINGS.GEO_RADIUS.UNIT') }}
                </p>
              </div>
              <div>
                <Input
                  v-model.number="geoRadiusRegion"
                  :label="
                    $t(
                      'LIBRARIES.EDIT.ADVANCED_SETTINGS.GEO_RADIUS.REGION_LABEL'
                    )
                  "
                  type="number"
                  placeholder="20000"
                />
                <p class="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                  {{ $t('LIBRARIES.EDIT.ADVANCED_SETTINGS.GEO_RADIUS.UNIT') }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="flex flex-row justify-end gap-2 py-2 px-0 w-full">
        <NextButton
          md
          slate
          ghost
          :label="$t('LIBRARIES.ADD.CANCEL_BUTTON_TEXT')"
          @click.prevent="onCancel"
        />
        <NextButton
          :is-loading="uiFlags.isCreating"
          md
          :label="$t('LIBRARIES.EDIT.KNOWLEDGEBASE')"
        />
      </div>
    </form>
  </div>
</template>

<style lang="scss" scoped>
:deep(.ProseMirror) {
  padding: 12px;
  min-height: 120px;
  outline: none;
  font-size: 14px;
  line-height: 1.5;
  color: #334155; /* slate-700 */
}

:deep(.dark .ProseMirror) {
  color: #cbd5e1; /* slate-300 */
}

:deep(.ProseMirror p) {
  margin: 0 0 8px 0;
}

:deep(.ProseMirror ul),
:deep(.ProseMirror ol) {
  padding-left: 20px;
  margin: 8px 0;
}

:deep(.ProseMirror li) {
  margin: 4px 0;
}

:deep(.ProseMirror strong) {
  font-weight: 600;
}

:deep(.ProseMirror em) {
  font-style: italic;
}

:deep(.ProseMirror code) {
  background-color: #f1f5f9; /* slate-100 */
  color: #1e293b; /* slate-800 */
  padding: 2px 4px;
  border-radius: 4px;
  font-size: 0.875em;
}

:deep(.dark .ProseMirror code) {
  background-color: #1e293b; /* slate-800 */
  color: #e2e8f0; /* slate-200 */
}

:deep(.ProseMirror pre) {
  background-color: #f1f5f9; /* slate-100 */
  color: #1e293b; /* slate-800 */
  padding: 12px;
  border-radius: 8px;
  overflow-x: auto;
  margin: 8px 0;
}

:deep(.dark .ProseMirror pre) {
  background-color: #1e293b; /* slate-800 */
  color: #e2e8f0; /* slate-200 */
}

:deep(.ProseMirror blockquote) {
  border-left: 4px solid #cbd5e1; /* slate-300 */
  padding-left: 16px;
  margin: 8px 0;
  font-style: italic;
}

:deep(.dark .ProseMirror blockquote) {
  border-left-color: #475569; /* slate-600 */
}

:deep(.ProseMirror h1),
:deep(.ProseMirror h2),
:deep(.ProseMirror h3),
:deep(.ProseMirror h4),
:deep(.ProseMirror h5),
:deep(.ProseMirror h6) {
  font-weight: 600;
  margin: 16px 0 8px 0;
  line-height: 1.3;
}

:deep(.ProseMirror h1) {
  font-size: 1.5em;
}

:deep(.ProseMirror h2) {
  font-size: 1.3em;
}

:deep(.ProseMirror h3) {
  font-size: 1.2em;
}

:deep(.ProseMirror h4) {
  font-size: 1.1em;
}

:deep(.ProseMirror h5) {
  font-size: 1em;
}

:deep(.ProseMirror h6) {
  font-size: 0.9em;
}
</style>
